import React, {useState} from 'react';

import {WebflowEnvironments} from '@packages/systems/core-constants';
import {USYS_DATA_ATTRS} from '@packages/systems/users/constants';
import {
  getBooleanOrElse,
  getArrayOrElse,
  getInPojo,
  getString,
} from '@packages/utilities/data-utils';
import {AtomRenderProps} from '@packages/systems/wfdl';

import {
  CHECKED_CLASS,
  FOCUSED_CLASS,
  FOCUSED_VISIBLE_CLASS,
  HIDE_DEFAULT_INPUT_STYLES,
} from '@packages/shared/render/plugins/Form/constants';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';

type NoopSetState = React.Dispatch<React.SetStateAction<boolean>>;
const noop: NoopSetState = (
  _v: boolean | ((prev: boolean) => boolean)
): void => {};
const mockUseStateForSsr = (initialValue: boolean): [boolean, NoopSetState] => [
  initialValue,
  noop,
];

const UserFormCustomCheckboxInput = ({
  attributes,
  classes,
  generateAttributes,
  data,
}: AtomRenderProps) => {
  const propsIsChecked =
    getBooleanOrElse(
      getInPojo(data, ['temp', 'checkbox', 'isChecked']),
      false
    ) ||
    getBooleanOrElse(getInPojo(data, ['attr', 'checked']), false) ||
    Boolean(
      getArrayOrElse(getInPojo(data, ['xattr']), []).find((attr) => {
        return (
          getInPojo(attr, ['name']) === 'checked' &&
          getInPojo(attr, ['value']) === 'checked'
        );
      })
    );
  const isPreviewMode = usePreviewMode();
  const propsIsFocused = getBooleanOrElse(
    getInPojo(data, ['temp', 'checkbox', 'isFocused']),
    false
  );
  const propsIsFocusedVisible = getBooleanOrElse(
    getInPojo(data, ['temp', 'checkbox', 'isFocusedVisible']),
    false
  );

  const runtimeEnvironment = useRuntimeEnvironment();

  const useStateHook =
    // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
    runtimeEnvironment === WebflowEnvironments.SSR
      ? mockUseStateForSsr
      : // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
        useState;

  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  const [isChecked, setIsChecked] = useStateHook(propsIsChecked);
  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  const [isFocused, setIsFocused] = useStateHook(propsIsFocused);
  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  const [isFocusedVisible, setIsFocusedVisible] = useStateHook(
    propsIsFocusedVisible
  );
  const wasClicked = React.useRef(false);

  const field = getString(getInPojo(data, ['users', 'field']));
  const fieldType = getString(getInPojo(data, ['users', 'fieldType']));

  const attributeWithFieldType = field
    ? attributes.set(USYS_DATA_ATTRS.field, field)
    : attributes;

  const props = generateAttributes(
    fieldType
      ? attributeWithFieldType.set(USYS_DATA_ATTRS.fieldType, fieldType)
      : attributeWithFieldType,
    classes
  );

  const {
    className,
    'data-w-id': dataWId,
    'data-wf-id': dataWfId,
    ...rest
  } = props;

  const inputProps = {
    ...rest,
    onChange: () => {
      setIsChecked((prevIsChecked: boolean) => !prevIsChecked);
    },
    onClick: () => {
      wasClicked.current = true;
    },
    onFocus: () => {
      setIsFocused(true);
      if (!wasClicked.current) {
        setIsFocusedVisible(true);
      }
    },
    onBlur: () => {
      setIsFocused(false);
      setIsFocusedVisible(false);
      wasClicked.current = false;
    },
  } as const;

  const pseudoModeClasses = isPreviewMode
    ? `${isChecked ? ` ${CHECKED_CLASS}` : ''}${
        isFocused ? ` ${FOCUSED_CLASS}` : ''
      }${isFocusedVisible ? ` ${FOCUSED_CLASS} ${FOCUSED_VISIBLE_CLASS}` : ''}`
    : `${propsIsChecked ? ` ${CHECKED_CLASS}` : ''}${
        propsIsFocused ? ` ${FOCUSED_CLASS}` : ''
      }${
        propsIsFocusedVisible
          ? ` ${FOCUSED_CLASS} ${FOCUSED_VISIBLE_CLASS}`
          : ''
      }`;

  const currentClassName = `${className}${pseudoModeClasses}`;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={currentClassName}
        data-w-id={dataWId}
        data-wf-id={dataWfId}
        onClick={isPreviewMode ? inputProps.onChange : undefined}
      />
      <input {...inputProps} style={HIDE_DEFAULT_INPUT_STYLES} />
    </>
  );
};

export default UserFormCustomCheckboxInput;
